import { ChangeDetectorRef, Component, inject, ChangeDetectionStrategy } from '@angular/core';
import type { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { FormControlDirective, Validators } from '@angular/forms';

import type { EditorState } from 'prosemirror-state';

import { TranslatePipe } from '@core/i18n-legacy';

import { WysiwygComponent } from '../wysiwyg/wysiwyg.component';

function smsCharacterLimitValidator(isInvalid: boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (!value) {
      return null;
    }

    return isInvalid ? { characterLimit: true } : null;
  };
}

@Component({
  selector: 'rt-wysiwyg-sms-counter',
  styleUrls: ['./wysiwyg-sms-counter.component.less'],
  template: `
    <span class="character-counter" [class.invalid]="invalid">
      {{ characterCount }} / {{ characterLimit }}
      @if (invalid) {
        <span> | {{ 'app.common.characters_limit_exceeded' | translate }}</span>
      }
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TranslatePipe],
})
export class WysiwygSmsCounterComponent {
  private readonly wysiwygComponent = inject<WysiwygComponent<any>>(WysiwygComponent);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly ngControl = inject(FormControlDirective, { optional: true, skipSelf: true });

  public characterCount: number = 0;
  public gsm7CharacterLimit: number = 1440;
  public ucs2CharacterLimit: number = 630;
  public characterLimit: number = this.gsm7CharacterLimit;
  public invalid: boolean = false;

  public ngOnInit() {
    this.calculateCharacters(this.wysiwygComponent.editorView.state);
    this.wysiwygComponent.stateChanges$.subscribe(state => {
      this.calculateCharacters(state);
    });
  }

  private calculateCharacters(state: EditorState) {
    this.getSmsSegmentsCalculator().then(SegmentedMessage => {
      const text = state.doc.textBetween(0, state.doc.content.size, undefined, ' ');
      const segmentedMessage = new SegmentedMessage(text);
      this.characterCount = segmentedMessage.numberOfCharacters;
      if (segmentedMessage.encodingName === 'GSM-7') this.characterLimit = this.gsm7CharacterLimit;
      if (segmentedMessage.encodingName === 'UCS-2') this.characterLimit = this.ucs2CharacterLimit;
      const isInvalid = this.characterCount > this.characterLimit;
      this.wysiwygComponent.toggleClass('invalid', isInvalid);
      this.invalid = isInvalid;
      this.ngControl!.control.setValidators([
        Validators.required,
        smsCharacterLimitValidator(this.invalid),
      ]);
      this.ngControl!.control.updateValueAndValidity();
      this.cdRef.detectChanges();
    });
  }

  private getSmsSegmentsCalculator() {
    /**
     * It is module.default.SegmentedMessage when built from libs and
     * module.SegmentedMessage without libraries prebuild.
     */
    return import('sms-segments-calculator').then(module =>
      module.default ? module.default.SegmentedMessage : module.SegmentedMessage,
    );
  }
}
